import { Link } from "react-router-dom";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { StatCard } from "../../components/admin/StatCard";
import { Badge } from "../../components/ui/Badge";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { useAdminMetrics } from "../../hooks/useAdminMetrics";
import { statusTone, titleCase } from "../../lib/format";

function countFor(source: Record<string, number> | undefined, key: string) {
  return source?.[key] ?? 0;
}

export function AdminPage() {
  const { data: metrics, loading, error } = useAdminMetrics();

  if (loading) return <FullPageLoader label="Loading admin control" />;
  if (error) return <EmptyState eyebrow="Admin" title="Unable to load dashboard.">{error}</EmptyState>;

  const tenantCounts = metrics?.tenant_counts ?? {};
  const roleCounts = metrics?.role_counts ?? {};
  const inviteCounts = metrics?.invite_counts ?? {};
  const tenantTotal = Object.values(tenantCounts).reduce((a, b) => a + b, 0);
  const warningTotal = (metrics?.tenants_without_owner ?? 0) + (metrics?.tenants_without_active_members ?? 0);

  return (
    <div>
      <AdminPageHeader
        title="Platform Admin"
        description="A focused control surface for tenants, users, invites and rollout state."
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Tenants" value={tenantTotal} detail={`${countFor(tenantCounts, "trial")} trial`} />
        <StatCard label="Users" value={metrics?.user_count ?? 0} detail={`${metrics?.disabled_users ?? 0} disabled`} />
        <StatCard label="Pending invites" value={countFor(inviteCounts, "pending")} detail={`${metrics?.pending_invites_older_than_7_days ?? 0} stale`} />
        <StatCard label="Data warnings" value={warningTotal} detail="owner and membership checks" />
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-[1fr_380px]">
        <AdminPanel title="Recent activity">
          <div className="space-y-4">
            {metrics?.recent_tenants?.slice(0, 4).map((tenant) => (
              <div className="flex items-center justify-between gap-4 rounded-md border border-line bg-field px-4 py-3" key={tenant.id}>
                <div>
                  <Link className="font-semibold text-ink underline-offset-4 hover:underline" to={`/admin/tenants/${tenant.id}`}>
                    {tenant.name}
                  </Link>
                  <p className="mt-1 text-sm text-graphite/60">{tenant.slug} - {tenant.plan}</p>
                </div>
                <Badge tone={statusTone(tenant.status)}>{tenant.status}</Badge>
              </div>
            ))}
            {!metrics?.recent_tenants?.length ? <p className="text-sm text-graphite/65">No tenant activity yet.</p> : null}
          </div>
        </AdminPanel>

        <div className="space-y-6">
          <AdminPanel title="Role mix">
            <div className="space-y-3 text-sm">
              {["platform_admin", "tenant_owner", "trainer", "assistant", "client"].map((role) => (
                <div className="flex items-center justify-between" key={role}>
                  <span className="text-graphite/70">{titleCase(role)}</span>
                  <strong className="text-ink">{countFor(roleCounts, role)}</strong>
                </div>
              ))}
            </div>
          </AdminPanel>
          <AdminPanel title="Control links">
            <div className="grid gap-2 text-sm">
              <Link className="link" to="/admin/invites">Create invite</Link>
              <Link className="link" to="/admin/tenants">Manage tenants</Link>
              <Link className="link" to="/admin/users">Manage users</Link>
              <Link className="link" to="/admin/feature-flags">Feature flags</Link>
              <Link className="link" to="/admin/data-health">Data health</Link>
            </div>
          </AdminPanel>
        </div>
      </div>
    </div>
  );
}
